// Imports for external libraries go here.
import { FC, useEffect, useState } from 'react';

import { LazyQueryHookOptions, useLazyQuery } from '@apollo/client';
import { phoenixLegalConsentsListQuery } from '@marriott/mi-legal-graphql';
import { getApplicableAndSortedConsents, isEmpty } from '../../utils/consents';
import { ConsentsList } from './ConsentsList';
import { ConsentErrorType } from './ConsentsList/ConsentsList.types';
import { ConsentsItems, LegalConsent, LegalConsentsProps, PhoenixLegalConsentsListType } from './LegalConsents.types';
import { Messages } from '@marriott/mi-ui-library';
import { StyledLegalConsents } from './LegalConsents.styles';
import { LegalConsentsAuthorModeAndApiErrorHandler } from './LegalConsentsAuthorModeAndApiErrorHandler';
import { generateApolloClientHeadersForUXLCalls } from '../../utils/uxlHelper';
import { useFocusOut } from '../../hooks/useFocusOut';
// import { mockLegalConsentsProps } from './__mock__/LegalConsents.mock';

const DEFAULT_CONSENT_ITEM: ConsentsItems = {
  gCloseAccordionBtnLabel: null,
  gOpenAccordionBtnLabel: null,
  gOpenInAModalSuffix: null,
  gOpenInANewTabSuffix: null,
  consentsList: [],
};

export const LegalConsents: FC<LegalConsentsProps> = ({
  sessionId,
  countryCode,
  isValidateErrors,
  locale,
  version = 'v1',
  customLoader,
  option,
  preState,
  consentsState,
  isAuthorMode,
  anchorBehavior = 'default',
  linkBehavior = 'default',
  setConsentsState,
  onConsentChange,
  onApiError,
  onModalClose,
  onLoading,
}) => {
  const [consentsResponse, setConsentsResponse] = useState<ConsentsItems>(DEFAULT_CONSENT_ITEM);
  // const loading = false;
  // const error = false;
  useFocusOut({
    onFocusOut: (isFocusOut: boolean) => {
      if (isFocusOut && onModalClose) {
        onModalClose();
      }
    },
    handleClick: false,
    handleKeyup: true,
    enableModal: true,
  });
  const [getLegalConsentsUxl, { loading, error }] =
    useLazyQuery<PhoenixLegalConsentsListType>(phoenixLegalConsentsListQuery);

  const getLegalConsents = () => {
    const uxlOptions: LazyQueryHookOptions = {
      variables: { locale, version },
      context: generateApolloClientHeadersForUXLCalls(sessionId, 'phoenixLegalConsentsListQuery', locale),
      fetchPolicy: 'cache-first',
    };
    return getLegalConsentsUxl(uxlOptions);
  };

  // const getLegalConsents = () => {
  //   const uxlOptions: LazyQueryHookOptions = {
  //     variables: { locale, version },
  //     context: {
  //       // uri: 'https://marriott-sites2-qa.adobecqms.net/graphql/execute.json/legal-consents/Locale-specific',
  //       uri: 'https://marriott-sites2-qa.adobecqms.net/graphql/execute.json/legal-consents/v2',
  //       // uri: '/graphql/execute.json/legal-consents/getCountryConsents',
  //       headers: {
  //         'x-request-id': `Phoenix-Legal-${sessionId}`,
  //       },
  //     },
  //     fetchPolicy: 'network-only',
  //   };
  //   return getLegalConsentsUxl(uxlOptions);
  // };

  useEffect(() => {
    if (!error) return;

    setConsentsResponse(DEFAULT_CONSENT_ITEM);
    setConsentsState({
      sectionTypes: {},
      list: [],
      consentsLedger: {},
      error: { type: ConsentErrorType.UXL_API_FAILURE, message: null },
    });
  }, [error]);

  useEffect(() => {
    const fetchLegalConsents = async () => {
      // const sectionItem = mockLegalConsentsProps.data?.consentsSectionList.items[0] || DEFAULT_CONSENT_ITEM;
      const legalConsentsRep = await getLegalConsents();
      const sectionItem =
        legalConsentsRep.data?.content?.legal?.legalConsentsByLocale?.aemData?.data?.consentsSectionList?.items[0] ||
        DEFAULT_CONSENT_ITEM;

      const {
        consentsList,
        gOpenInAModalSuffix,
        gOpenInANewTabSuffix,
        gCloseAccordionBtnLabel,
        gOpenAccordionBtnLabel,
      } = sectionItem;

      console.log('ConsentFramework: fetchLegalConsents payload ', {
        sessionId,
        countryCode,
        isValidateErrors,
        locale,
        option,
        consentsState,
        isAuthorMode,
        anchorBehavior,
        linkBehavior,
      });

      console.log('ConsentFramework: fetchLegalConsents response ', legalConsentsRep);

      setConsentsResponse({
        consentsList,
        gCloseAccordionBtnLabel,
        gOpenAccordionBtnLabel,
        gOpenInAModalSuffix,
        gOpenInANewTabSuffix,
      });
    };
    !isAuthorMode && fetchLegalConsents();
  }, [locale, isAuthorMode, version]);

  // If option changes then
  // We don't have to call the API again to to fetch the data
  // Pass the same response as previous and filter out applicable consent list with respect to new option value
  useEffect(() => {
    // skip until API finishes OR still default state
    if (loading || consentsResponse === DEFAULT_CONSENT_ITEM) return;

    const {
      consentsList: allConsents,
      gOpenInAModalSuffix: ariaModalText,
      gOpenInANewTabSuffix: ariaWindowText,
    } = consentsResponse;

    if (isEmpty(allConsents)) {
      setConsentsState({
        sectionTypes: {},
        list: [] as LegalConsent[],
        consentsLedger: {},
        error: { type: ConsentErrorType.INVALID_RESPONSE, message: null },
      });
      return;
    }

    const sortedConsentListData = getApplicableAndSortedConsents({
      preState,
      allConsents,
      countryCode,
      option,
      anchorBehavior,
      ariaModalText,
      ariaWindowText,
    });

    console.log('ConsentFramework: sortedConsents payload ', {
      sessionId,
      countryCode,
      isValidateErrors,
      locale,
      option,
      consentsState,
      isAuthorMode,
      anchorBehavior,
      linkBehavior,
    });
    console.log('ConsentFramework: sortedConsents Data ', sortedConsentListData);

    setConsentsState(sortedConsentListData);
  }, [option, countryCode, consentsResponse, loading, preState]);

  useEffect(() => {
    onLoading?.(loading);
  }, [loading, onLoading]);

  const isValidationErrorFound = consentsState.error.type === ConsentErrorType.INTERNAL_VALIDATION_ERROR;

  if (isAuthorMode || error) {
    return (
      <LegalConsentsAuthorModeAndApiErrorHandler
        isAuthorMode={Boolean(isAuthorMode)}
        isApiError={Boolean(error)}
        onApiError={onApiError}
      />
    );
  }

  return (
    <StyledLegalConsents data-component-name="o-legal-LegalConsents" className="m-legal-consents-container">
      {loading && (customLoader || <div data-testid="skeleton-loader" className="skeleton-loader"></div>)}
      {isValidationErrorFound && (
        <Messages messageType="error-sev1" className="mb-4" messageText={consentsState.error.message!} />
      )}
      {!loading && (
        <ConsentsList
          consentState={consentsState}
          anchorBehavior={anchorBehavior}
          linkBehavior={linkBehavior}
          onConsentChange={onConsentChange}
          validateErrors={isValidateErrors}
          closeAccordionBtnLabel={consentsResponse.gCloseAccordionBtnLabel}
          openAccordionBtnLabel={consentsResponse.gOpenAccordionBtnLabel}
        />
      )}
    </StyledLegalConsents>
  );
};
